export default function QuietTitleSection() {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Understanding <strong>Quiet Title Actions</strong>
          </h2>
          <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            A quiet title action is a lawsuit that "quiets" competing claims to property ownership. It's often necessary when <strong>selling a house without clear title</strong>, but it's expensive and time-consuming. Here's what you need to know.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">When Is a Quiet Title Action Needed?</h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              You may need a <strong>quiet title action for heirs</strong> or other situations when:
            </p>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">Competing Ownership Claims</p>
                  <p className="text-gray-700">Multiple people claim to own the property or portions of it</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">Unresolved Liens</p>
                  <p className="text-gray-700">Old liens that can't be released because the lien holder is defunct or can't be located</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">Boundary Disputes</p>
                  <p className="text-gray-700">Neighbors disagree about property lines and won't negotiate</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">Adverse Possession</p>
                  <p className="text-gray-700">Someone claims ownership through long-term use of the property</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">Errors in Public Records</p>
                  <p className="text-gray-700">Recording mistakes that create uncertainty about ownership</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">Missing or Unknown Heirs</p>
                  <p className="text-gray-700">Heirs who can't be located but have potential ownership interests</p>
                </div>
              </li>
            </ul>
          </div>
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/addb05ce49e94b826e999bb180d62155.jpg"
              alt="Quiet title action courtroom proceedings"
              className="w-full h-auto rounded-lg shadow-2xl"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8 md:p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            The Quiet Title Process in Texas
          </h3>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">File Lawsuit</h4>
              <p className="text-gray-700 text-sm leading-relaxed">
                Attorney files a quiet title lawsuit in district court, naming all parties with potential claims to the property.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Serve Notice</h4>
              <p className="text-gray-700 text-sm leading-relaxed">
                All parties must be properly served with notice. Unknown parties are served through publication in newspapers.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Discovery & Hearing</h4>
              <p className="text-gray-700 text-sm leading-relaxed">
                Evidence is gathered, depositions taken, and a hearing is held where the judge reviews all claims.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold text-white">4</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Court Order</h4>
              <p className="text-gray-700 text-sm leading-relaxed">
                Judge issues an order declaring who owns the property and eliminating competing claims.
              </p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-red-900 mb-6 flex items-center gap-3">
              <i className="ri-error-warning-line text-3xl"></i>
              The Downsides of Quiet Title Actions
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">Expensive</p>
                  <p className="text-gray-700 text-sm">Attorney fees typically range from $5,000 to $15,000+, plus court costs and filing fees</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">Time-Consuming</p>
                  <p className="text-gray-700 text-sm">Takes 6-18 months from filing to final order, during which you can't sell the property</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">Uncertain Outcome</p>
                  <p className="text-gray-700 text-sm">Court decisions can be unpredictable, especially in complex cases with multiple parties</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">Ongoing Costs</p>
                  <p className="text-gray-700 text-sm">You still pay property taxes, insurance, and maintenance while the lawsuit proceeds</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">No Guarantee of Sale</p>
                  <p className="text-gray-700 text-sm">Even after winning, you still need to find a buyer willing to purchase the property</p>
                </div>
              </li>
            </ul>
          </div>
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-emerald-900 mb-6 flex items-center gap-3">
              <i className="ri-check-line text-3xl"></i>
              Our Alternative Approach
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">No Out-of-Pocket Costs</p>
                  <p className="text-gray-700 text-sm">We handle all legal work and pay for any necessary quiet title actions after closing</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">Fast Closing</p>
                  <p className="text-gray-700 text-sm">Close in 7-14 days instead of waiting 6-18 months for court proceedings</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">Guaranteed Sale</p>
                  <p className="text-gray-700 text-sm">We buy the property regardless of title issues - you don't have to find another buyer</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">Stop Carrying Costs</p>
                  <p className="text-gray-700 text-sm">No more paying taxes, insurance, and maintenance on a property you can't sell</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">Cash in Hand</p>
                  <p className="text-gray-700 text-sm">Walk away with money now instead of spending thousands on legal fees with uncertain results</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8 md:p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            When We Handle Quiet Title Actions
          </h3>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                In some cases, we'll purchase your property and then file a quiet title action ourselves after closing. This approach benefits you because:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                  <p className="text-gray-700">You get paid immediately without waiting for the lawsuit to complete</p>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                  <p className="text-gray-700">We pay all attorney fees and court costs</p>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                  <p className="text-gray-700">We take on all the risk and uncertainty of the legal process</p>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                  <p className="text-gray-700">You're free from the property and can move on with your life</p>
                </li>
              </ul>
              <p className="text-lg text-gray-700 leading-relaxed">
                We have the resources and experience to handle complex quiet title actions. We've successfully cleared titles that other buyers said were impossible to fix.
              </p>
            </div>
            <div className="bg-emerald-500 text-white p-8 rounded-lg">
              <h4 className="text-2xl font-bold mb-4">Real Example:</h4>
              <p className="mb-4 leading-relaxed">
                We purchased a property in El Paso with a 30-year-old lien from a defunct contractor. The lien holder's company no longer existed, and there was no way to get a release.
              </p>
              <p className="mb-4 leading-relaxed">
                We bought the property for $135,000, then filed a quiet title action to remove the lien. The lawsuit took 8 months and cost $7,500 in legal fees.
              </p>
              <p className="font-semibold">
                The seller got $135,000 cash immediately and never had to worry about the lien again. We handled everything after closing.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}